// src/pages/admin/products/RawMaterialsInventory.jsx
import { useState, useEffect } from 'react'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  Trash2,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Building,
  TrendingDown,
  TrendingUp,
  DollarSign,
  ChevronDown
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import RawMaterialInventoryService from '../../../services/rawMaterialInventoryService'
import BranchService from '../../../services/branchService'
import { usePermissions } from '../../../hooks/usePermissions'
import AuthService from '../../../services/authService'
import AssignInventoryModal from '../../../components/modals/AssignInventoryModal'
import EditInventoryModal from '../../../components/modals/EditInventoryModal'
import ViewInventoryModal from '../../../components/modals/ViewInventoryModal'
import DeleteConfirmModal from '../../../components/modals/DeleteConfirmModal'
import CardLoader from '../../../components/ui/CardLoader'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 
    type === 'error' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : <Package className="h-5 w-5" />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

export const RawMaterialsInventory = () => {
  // Permission hooks
  const currentUser = AuthService.getCurrentUser()
  const permissions = usePermissions(currentUser)
  const isSuperAdmin =
    currentUser?.role?.toLowerCase() === 'super_admin' ||
    currentUser?.role?.toLowerCase() === 'superadmin'

  // State management
  const [inventories, setInventories] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [branchFilter, setBranchFilter] = useState('all')
  const [sortBy, setSortBy] = useState('rawMaterial.name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [toast, setToast] = useState(null)

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedInventory, setSelectedInventory] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Safe getter utility
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate inventory object
  const validateInventory = (inventory) => {
    if (!inventory || typeof inventory !== 'object') return false
    // Check for required fields based on your actual API response
    return inventory._id && inventory.rawMaterial && inventory.branch
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    // Don't reset branch filter for restricted users (managers)
    if (!permissions.isBranchRestricted) {
      setBranchFilter('all')
    }
    setSortBy('rawMaterial.name')
    setSortOrder('asc')
    setCurrentPage(1)
  }

  // Initial load
  useEffect(() => {
    // Fetch inventories using service
    const fetchInventories = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Log manager access
        if (permissions.logBranchAccess) {
          permissions.logBranchAccess('raw_material_inventory', 'view')
        }
        
        // Check if user is branch-restricted
        const isRestricted = permissions.isBranchRestricted
        const userBranchId = currentUser?.branchId
        
        // Apply branch filtering for restricted users (managers)
        let inventoryParams = {}
        if (isRestricted && userBranchId) {
          inventoryParams.branchId = userBranchId
          console.log(`🏢 [MANAGER FILTER] Loading raw material inventory for branch: ${userBranchId}`)
        }
        
        const response = await RawMaterialInventoryService.getAllInventories(inventoryParams)
        
        if (response && response.success) {
          // Based on your API response, inventories are in response.data.rawMaterialInventories
          const inventoriesData = safeGet(response, 'data.rawMaterialInventories', [])
          
          console.log('📦 Inventory response structure:', response.data)
          console.log('📦 Extracted inventories data:', inventoriesData)
          console.log('📦 Number of inventories found:', inventoriesData.length)
          
          // Log sample inventory structure to understand the data
          if (inventoriesData.length > 0) {
            console.log('📦 Sample inventory structure:', inventoriesData[0])
            console.log('📦 Sample material name:', inventoriesData[0].rawMaterial?.name)
            console.log('📦 Sample branch name:', inventoriesData[0].branch?.name)
            console.log('📦 Sample unit abbreviation:', inventoriesData[0].rawMaterial?.unit?.abbreviation)
          }
          
          // Filter out invalid inventories with more flexible validation
          let validInventories = Array.isArray(inventoriesData) 
            ? inventoriesData.filter(inventory => {
                const isValid = validateInventory(inventory)
                if (!isValid) {
                  console.log('❌ Invalid inventory found:', inventory)
                }
                return isValid
              })
            : []
          
          // Additional client-side filtering for branch-restricted users
          if (isRestricted && userBranchId) {
            validInventories = validInventories.filter(inventory => 
              inventory.branchId === userBranchId || 
              (inventory.branch && inventory.branch._id === userBranchId)
            )
            console.log(`🔒 [BRANCH RESTRICTED] Showing ${validInventories.length} raw material inventory items for branch ${userBranchId}`)
          }
          
          console.log('📦 Valid inventories loaded:', validInventories.length)
          setInventories(validInventories)
        } else {
          console.warn('⚠️ Unexpected response format:', response)
          setError('Failed to load inventories')
          setInventories([])
        }
      } catch (error) {
        console.error('❌ Error fetching inventories:', error)
        setError(error.message || 'Failed to load inventories')
        setInventories([])
      } finally {
        setLoading(false)
      }
    }

    // Fetch branches for dropdown
    const fetchBranches = async () => {
      try {
        const response = await BranchService.getAllBranches()
        if (response && response.success) {
          const branchesData = safeGet(response, 'data.branches', [])
          let filteredBranches = Array.isArray(branchesData) ? branchesData : []
          
          // Filter branches for restricted users (managers)
          const isRestricted = permissions.isBranchRestricted
          const userBranchId = currentUser?.branchId
          
          if (isRestricted && userBranchId) {
            filteredBranches = branchesData.filter(branch => branch._id === userBranchId)
            console.log(`🏢 [BRANCH RESTRICTED] Manager can only see their branch: ${filteredBranches[0]?.name || userBranchId}`)
          }
          
          setBranches(filteredBranches)
        }
      } catch (error) {
        console.error('❌ Error fetching branches:', error)
      }
    }

    const loadData = async () => {
      console.log('🚀 Starting inventory data load...')
      await fetchBranches()
      await fetchInventories()
      console.log('✅ Inventory data load complete')
    }
    
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array to prevent infinite loops caused by permissions/currentUser object changes

  // Simple refresh function for manual refresh operations
  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Log manager access
      if (permissions.logBranchAccess) {
        permissions.logBranchAccess('raw_material_inventory', 'view')
      }
      
      // Check if user is branch-restricted
      const isRestricted = permissions.isBranchRestricted
      const userBranchId = currentUser?.branchId
      
      // Apply branch filtering for restricted users (managers)
      let inventoryParams = {}
      if (isRestricted && userBranchId) {
        inventoryParams.branchId = userBranchId
        console.log(`🏢 [MANAGER FILTER] Loading raw material inventory for branch: ${userBranchId}`)
      }
      
      const response = await RawMaterialInventoryService.getAllInventories(inventoryParams)
      
      if (response && response.success) {
        const inventoriesData = safeGet(response, 'data.rawMaterialInventories', [])
        
        let validInventories = Array.isArray(inventoriesData) 
          ? inventoriesData.filter(inventory => validateInventory(inventory))
          : []
        
        // Additional client-side filtering for branch-restricted users
        if (isRestricted && userBranchId) {
          validInventories = validInventories.filter(inventory => 
            inventory.branchId === userBranchId || 
            (inventory.branch && inventory.branch._id === userBranchId)
          )
        }
        
        setInventories(validInventories)
      } else {
        setError('Failed to load inventories')
        setInventories([])
      }
    } catch (error) {
      console.error('❌ Error fetching inventories:', error)
      setError(error.message || 'Failed to load inventories')
      setInventories([])
    } finally {
      setLoading(false)
    }
  }

  // Auto-set branch filter for managers
  useEffect(() => {
    if (permissions.isBranchRestricted && currentUser?.branchId) {
      setBranchFilter(currentUser.branchId)
      console.log(`🎯 [AUTO FILTER] Setting branch filter to manager's branch: ${currentUser.branchId}`)
    }
  }, [permissions.isBranchRestricted, currentUser?.branchId])

  // Get stock status
  const getStockStatus = (currentStock, threshold) => {
    if (currentStock === 0) {
      return 'out_of_stock'
    } else if (currentStock <= threshold) {
      return 'low_stock'
    } else if (currentStock > threshold * 3) {
      return 'over_stock'
    } else {
      return 'normal'
    }
  }

  // Get status color classes
  const getStatusColor = (status) => {
    const colors = {
      normal: 'bg-blue-100 text-blue-800',
      low_stock: 'bg-yellow-100 text-yellow-800',
      out_of_stock: 'bg-red-100 text-red-800',
      over_stock: 'bg-green-100 text-green-800'
    }
    return colors[status] || colors.normal
  }

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      normal: 'Normal',
      low_stock: 'Low Stock',
      out_of_stock: 'Out of Stock',
      over_stock: 'Over Stock'
    }
    return labels[status] || 'Unknown'
  }

  // Filter and search inventories
  const getFilteredInventories = () => {
    let filtered = [...inventories]

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(inventory => {
        const materialName = safeGet(inventory, 'rawMaterial.name', '').toLowerCase()
        const materialCode = safeGet(inventory, 'rawMaterial.code', '').toLowerCase()
        const branchName = safeGet(inventory, 'branch.name', '').toLowerCase()
        
        return materialName.includes(searchLower) ||
               materialCode.includes(searchLower) ||
               branchName.includes(searchLower)
      })
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inventory => {
        const threshold = safeGet(inventory, 'rawMaterial.threshold', 0)
        const status = getStockStatus(inventory.currentStock, threshold)
        return status === statusFilter
      })
    }

    // Branch filter
    if (branchFilter !== 'all') {
      filtered = filtered.filter(inventory => {
        const branchId = safeGet(inventory, 'branch._id', inventory.branch)
        return branchId === branchFilter
      })
    }

    // Sort
    filtered.sort((a, b) => {
      let valueA, valueB
      
      // Handle nested properties
      if (sortBy.includes('.')) {
        const keys = sortBy.split('.')
        valueA = keys.reduce((obj, key) => obj?.[key], a)
        valueB = keys.reduce((obj, key) => obj?.[key], b)
      } else {
        valueA = a[sortBy]
        valueB = b[sortBy]
      }

      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB?.toLowerCase() || ''
      }

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })

    return filtered
  }

  // Get paginated inventories
  const getPaginatedInventories = () => {
    const filtered = getFilteredInventories()
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filtered.slice(startIndex, endIndex)
  }

  // Get pagination info
  const getPaginationInfo = () => {
    const total = getFilteredInventories().length
    const totalPages = Math.ceil(total / itemsPerPage)
    return { total, totalPages }
  }

  // Calculate summary stats
  const getSummaryStats = () => {
    const summary = {
      total: inventories.length,
      normal: 0,
      lowStock: 0,
      outOfStock: 0,
      totalValue: 0
    }

    inventories.forEach(inventory => {
      const threshold = safeGet(inventory, 'rawMaterial.threshold', 0)
      const status = getStockStatus(inventory.currentStock, threshold)
      
      if (status === 'normal' || status === 'over_stock') {
        summary.normal++
      } else if (status === 'low_stock') {
        summary.lowStock++
      } else if (status === 'out_of_stock') {
        summary.outOfStock++
      }

      const value = (inventory.currentStock || 0) * (inventory.unitPrice || 0)
      summary.totalValue += value
    })

    return summary
  }

  // Handle inventory assigned
  const handleInventoryAssigned = (newInventories) => {
    if (Array.isArray(newInventories)) {
      setInventories(prev => [...newInventories, ...prev])
      showToast(`${newInventories.length} raw material(s) assigned successfully`)
    } else if (validateInventory(newInventories)) {
      setInventories(prev => [newInventories, ...prev])
      showToast('Raw material assigned successfully')
    }
    fetchData() // Refresh to get latest data
  }

  // Handle inventory updated
  const handleInventoryUpdated = (updatedInventory) => {
    if (validateInventory(updatedInventory)) {
      setInventories(prev => 
        prev.map(inventory => 
          inventory._id === updatedInventory._id ? updatedInventory : inventory
        )
      )
      showToast('Inventory updated successfully')
      fetchData()
    }
  }

  // Handle inventory deleted
  const handleInventoryDeleted = async () => {
    if (!selectedInventory) return

    try {
      const response = await RawMaterialInventoryService.deleteInventory(selectedInventory._id)
      
      if (response && response.success) {
        setInventories(prev => prev.filter(inventory => inventory._id !== selectedInventory._id))
        showToast('Inventory deleted successfully')
      } else {
        showToast('Failed to delete inventory', 'error')
      }
    } catch (error) {
      console.error('❌ Error deleting inventory:', error)
      showToast(error.message || 'Failed to delete inventory', 'error')
    }

    setShowDeleteModal(false)
    setSelectedInventory(null)
  }

  // Handle status toggle
  // eslint-disable-next-line no-unused-vars
  const handleStatusToggle = async (inventory) => {
    try {
      const newStatus = !inventory.isActive
      const response = await RawMaterialInventoryService.toggleInventoryStatus(inventory._id, newStatus)
      
      if (response && response.success) {
        setInventories(prev => 
          prev.map(inv => 
            inv._id === inventory._id ? { ...inv, isActive: newStatus } : inv
          )
        )
        showToast(`Inventory ${newStatus ? 'activated' : 'deactivated'} successfully`)
      } else {
        showToast('Failed to update status', 'error')
      }
    } catch (error) {
      console.error('❌ Error toggling status:', error)
      showToast(error.message || 'Failed to update status', 'error')
    }
  }

  // Action handlers
  const handleView = (inventory) => {
    setSelectedInventory(inventory)
    setShowViewModal(true)
  }

  const handleEdit = (inventory) => {
    setSelectedInventory(inventory)
    setShowEditModal(true)
  }

  const handleDelete = (inventory) => {
    setSelectedInventory(inventory)
    setShowDeleteModal(true)
  }

  const { total, totalPages } = getPaginationInfo()
  const paginatedInventories = getPaginatedInventories()
  const summaryStats = getSummaryStats()
  const filteredData = getFilteredInventories()

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Raw Materials Inventory</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button 
            onClick={() => setShowAssignModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add to Inventory
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Normal Stock</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.normal}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low/Out Stock</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.lowStock + summaryStats.outOfStock}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">Rs. {summaryStats.totalValue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Inventory</h2>
            </div>
            {(searchTerm || statusFilter !== 'all' || (!permissions.isBranchRestricted && branchFilter !== 'all') || sortBy !== 'rawMaterial.name') && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search materials, codes, branches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Status</option>
                <option value="normal">Normal</option>
                <option value="low_stock">Low Stock</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="over_stock">Over Stock</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Branch Filter */}
            <div className="relative">
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                disabled={permissions.isBranchRestricted}
                className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white ${
                  permissions.isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="all">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-')
                  setSortBy(field)
                  setSortOrder(order)
                }}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="rawMaterial.name-asc">Material (A-Z)</option>
                <option value="rawMaterial.name-desc">Material (Z-A)</option>
                <option value="rawMaterial.code-asc">Code (A-Z)</option>
                <option value="rawMaterial.code-desc">Code (Z-A)</option>
                <option value="branch.name-asc">Branch (A-Z)</option>
                <option value="branch.name-desc">Branch (Z-A)</option>
                <option value="currentStock-asc">Stock (Low-High)</option>
                <option value="currentStock-desc">Stock (High-Low)</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing {filteredData.length} of {inventories.length} inventory items
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      {loading ? (
        <CardLoader title="Loading inventory..." />
      ) : error ? (
        <Card className="bg-white">
          <CardContent className="py-12">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={fetchData} leftIcon={RefreshCw}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white">
          <CardContent className="p-0">
            {filteredData.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No inventory items found</h3>
                <p className="text-gray-500 mb-4">
                  {inventories.length === 0 ? 'Get started by adding your first inventory item.' : 'Try adjusting your search criteria.'}
                </p>
                {inventories.length === 0 && (
                  <Button
                    onClick={() => setShowAssignModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Inventory
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MATERIAL
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CODE
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CURRENT STOCK
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        STATUS
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        BRANCH
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedInventories.map((inventory) => {
                      const material = inventory.rawMaterial
                      const branch = inventory.branch
                      const unit = material?.unit
                      const threshold = material?.threshold || 0
                      const status = getStockStatus(inventory.currentStock, threshold)
                      const currentStock = inventory.currentStock || 0
                      
                      return (
                        <tr key={inventory._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {material?.name || 'Unknown Material'}
                            </div>
                            {material?.description && (
                              <div className="text-sm text-gray-500">{material.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {material?.code || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900">
                                {currentStock.toLocaleString()} <span className="text-blue-600">{unit?.abbreviation || 'units'}</span>
                              </div>
                              {isSuperAdmin && (
                                <button
                                  onClick={() => handleEdit(inventory)}
                                  className="text-gray-300 hover:text-amber-600 p-0.5 rounded transition-colors"
                                  title="Edit Current Stock (Super Admin)"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                            {threshold > 0 && (
                              <div className="text-xs text-gray-500">
                                Threshold: {threshold} {unit?.abbreviation || 'units'}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                              {getStatusLabel(status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Building className="h-4 w-4 mr-2 text-gray-400" />
                              {branch?.name || 'Unknown Branch'}
                            </div>
                            {branch?.address && (
                              <div className="text-xs text-gray-500">
                                {branch.address.split(',')[0] || 'N/A'}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleView(inventory)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {isSuperAdmin && (
                                <button
                                  onClick={() => handleEdit(inventory)}
                                  className="text-gray-400 hover:text-amber-600 p-1 rounded transition-colors"
                                  title="Edit Stock (Super Admin Only)"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(inventory)}
                                className="text-gray-400 hover:text-red-600 p-1 rounded"
                                title="Delete Inventory"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} results
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAssignModal && (
        <AssignInventoryModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onInventoryAssigned={handleInventoryAssigned}
          branches={branches}
        />
      )}

      {showEditModal && selectedInventory && (
        <EditInventoryModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedInventory(null)
          }}
          inventory={selectedInventory}
          onInventoryUpdated={handleInventoryUpdated}
        />
      )}

      {showViewModal && selectedInventory && (
        <ViewInventoryModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false)
            setSelectedInventory(null)
          }}
          inventory={selectedInventory}
        />
      )}

      {showDeleteModal && selectedInventory && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedInventory(null)
          }}
          onConfirm={handleInventoryDeleted}
          title="Delete Inventory Item"
          message={`Are you sure you want to delete the inventory for "${selectedInventory.rawMaterial?.name}" at "${selectedInventory.branch?.name}"? This action cannot be undone.`}
        />
      )}
    </div>
  )
}